import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { OrganizationRoleSwitcher } from '@/components/layout/OrganizationRoleSwitcher';
import { authApi, legalDocumentsApi } from '@/lib/pillarApi';
import { buildRequiredTotpSetupPath } from '@/lib/requiredTotpSetup';
import {
    resetReactQueryAfterRoleSwitch,
    setSelectedRoleAssignmentInCache,
} from '@/lib/effectiveSessionRefresh';
import { invalidateSessionSensitiveRequests } from '@/lib/sessionSensitiveRequestManager';
import { runInBackground } from '@/lib/utils';
import { createComponentLogger } from '@/lib/logging/frontend-logger';
import { useAuthStore } from '@/store/auth';
import type {
    ApplicableTermsResult,
    LegalDocumentAssignmentPublic,
    SetActiveRoleAssignmentResponse,
} from '@pillar-portal/api-types';
import { LegalDocumentReview } from './LegalDocumentReview';

const ReturnToQuerySchema = z.object({
    returnTo: z
        .string()
        .startsWith('/')
        .refine((value) => !value.startsWith('//'), 'returnTo must be a same-origin path')
        .optional(),
});

export function LegalWorkflowPage() {
    const log = createComponentLogger('LegalWorkflowPage');
    const navigate = useNavigate();
    const location = useLocation();
    const queryClient = useQueryClient();
    const logoutStore = useAuthStore((state) => state.logout);
    const setIsLoggingOut = useAuthStore((state) => state.setIsLoggingOut);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const returnTo = useMemo(() => {
        const parseResult = ReturnToQuerySchema.safeParse({
            returnTo: new URLSearchParams(location.search).get('returnTo') ?? undefined,
        });
        return parseResult.success ? (parseResult.data.returnTo ?? '/dashboard') : '/dashboard';
    }, [location.search]);

    const applicableTermsQuery = useQuery<ApplicableTermsResult>({
        queryKey: ['applicableTerms'],
        queryFn: async () => await legalDocumentsApi.getApplicableTerms(),
        retry: false,
        staleTime: 0,
        refetchOnWindowFocus: false,
    });
    const applicableTerms = applicableTermsQuery.data;
    // User agreements are reviewed one at a time, in title order.
    const userAgreements =
        applicableTerms?.coverage_state === 'acceptance_required'
            ? applicableTerms.user_agreements
            : [];
    const requiredAssignment =
        userAgreements.find((agreement) => !agreement.accepted)?.legal_document_assignment ??
        null;
    const requiredPosition =
        userAgreements.filter((agreement) => agreement.accepted).length + 1;
    const finishActivation = (response: SetActiveRoleAssignmentResponse): void => {
        setSelectedRoleAssignmentInCache(queryClient, response.role_assignment_uuid);
        if (response.activation_state === 'mfa_setup_required') {
            runInBackground(
                navigate(buildRequiredTotpSetupPath(response.mfa_setup_activation_target), {
                    replace: true,
                }),
                (error: unknown) => log.error('Unable to navigate to MFA setup', error)
            );
            return;
        }
        if (response.activation_state === 'mfa_challenge_required') {
            const searchParams = new URLSearchParams();
            if (response.available_mfa_methods.length > 0) {
                searchParams.set('mfa_methods', response.available_mfa_methods.join(','));
            }
            const query = searchParams.toString();
            runInBackground(
                navigate(`/login/totp${query.length === 0 ? '' : `?${query}`}`, {
                    replace: true,
                }),
                (error: unknown) => log.error('Unable to navigate to MFA challenge', error)
            );
            return;
        }
        resetReactQueryAfterRoleSwitch(queryClient, log);
        runInBackground(navigate(returnTo, { replace: true }), (error: unknown) => {
            log.error('Unable to enter the operational application', error);
        });
    };

    const activateMutation = useMutation({
        mutationFn: async (roleAssignmentUuid: string) =>
            await authApi.setActiveRoleAssignment(roleAssignmentUuid),
        onSuccess: finishActivation,
        onError: (error: unknown) => {
            setErrorMessage(
                error instanceof Error
                    ? error.message
                    : 'Unable to continue after legal acceptance.'
            );
        },
    });
    const acceptMutation = useMutation({
        mutationFn: async (request: {
            roleAssignmentUuid: string;
            assignment: LegalDocumentAssignmentPublic;
        }) =>
            await legalDocumentsApi.acceptApplicableTerms({
                role_assignment_uuid: request.roleAssignmentUuid,
                legal_document_assignment_uuid:
                    request.assignment.legal_document_assignment_uuid,
                legal_document_uuid: request.assignment.legal_document.legal_document_uuid,
            }),
        onSuccess: (result) => {
            setErrorMessage(null);
            queryClient.setQueryData(['applicableTerms'], result);
            if (result.coverage_state === 'acceptance_required') {
                // The next pending agreement is shown for review.
                return;
            }
            activateMutation.mutate(result.role_assignment_uuid);
        },
        onError: (error: unknown) => {
            setErrorMessage(
                error instanceof Error ? error.message : 'Unable to accept the legal document.'
            );
        },
    });
    const logoutMutation = useMutation({
        mutationFn: authApi.logout,
        onMutate: () => setIsLoggingOut(true),
        onSettled: () => {
            logoutStore();
            invalidateSessionSensitiveRequests();
            queryClient.clear();
            runInBackground(navigate('/login', { replace: true }), (error: unknown) => {
                log.error('Unable to navigate after logout', error);
            });
        },
    });

    if (applicableTermsQuery.isLoading) {
        return <p className="p-6">Loading legal requirements...</p>;
    }
    if (applicableTermsQuery.isError || applicableTerms === undefined) {
        return (
            <div className="p-6" role="alert">
                <p>
                    Legal requirements could not be loaded. Ordinary product access remains
                    unavailable.
                </p>
                <Button className="mt-3" onClick={() => applicableTermsQuery.refetch()}>
                    Retry
                </Button>
            </div>
        );
    }

    const legalDocumentTitle =
        requiredAssignment?.legal_document.legal_document_title ?? 'Terms of Service';

    return (
        <div className="mx-auto max-w-6xl space-y-4 p-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold">{legalDocumentTitle}</h1>
                    <p className="text-sm text-gray-600">
                        Please review the document below. You can also switch to another role or log
                        out.
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <OrganizationRoleSwitcher />
                    <Button
                        variant="outline"
                        disabled={logoutMutation.isPending}
                        onClick={() => logoutMutation.mutate()}
                    >
                        Log out
                    </Button>
                </div>
            </div>

            {errorMessage === null ? null : (
                <div
                    role="alert"
                    className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700"
                >
                    {errorMessage}
                </div>
            )}

            {applicableTerms.coverage_state !== 'acceptance_required' ? (
                <Card>
                    <CardHeader>
                        <CardTitle>No documents to accept</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p className="text-sm">
                            There is nothing left to accept for this role. Continue to the
                            application.
                        </p>
                        <Button
                            className="mt-3"
                            disabled={activateMutation.isPending}
                            onClick={() =>
                                activateMutation.mutate(applicableTerms.role_assignment_uuid)
                            }
                        >
                            Continue
                        </Button>
                    </CardContent>
                </Card>
            ) : (
                <Card>
                    <CardHeader>
                        <CardTitle>
                            {userAgreements.length > 1
                                ? `Review document ${requiredPosition} of ${userAgreements.length}`
                                : 'Review the document'}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {requiredAssignment === null ? null : (
                            <LegalDocumentReview
                                legalDocumentUuid={
                                    requiredAssignment.legal_document.legal_document_uuid
                                }
                                title={legalDocumentTitle}
                                acceptLabel="I agree"
                                acceptPending={
                                    acceptMutation.isPending || activateMutation.isPending
                                }
                                onAccept={() =>
                                    acceptMutation.mutate({
                                        roleAssignmentUuid: applicableTerms.role_assignment_uuid,
                                        assignment: requiredAssignment,
                                    })
                                }
                            />
                        )}
                    </CardContent>
                </Card>
            )}
        </div>
    );
}
